// Copyright 2026, Auxilio Technologies LLC

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ordersAPI, Order } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { hasPermission, RoleType, Permissions } from '../config/rolesConfig';
import { STATUS_BADGE, SLOT_ORDER, ORDER_STATUSES, formatMoney, getLocalDateString } from '../lib/orderUtils';
import { PlusIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const OrderBoard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const role = (user?.role || 'viewer') as RoleType;
  const canWrite = hasPermission(role, Permissions.ORDERS_WRITE);

  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [slotFilter, setSlotFilter] = useState('');
  const [dateFilter, setDateFilter] = useState(getLocalDateString());

  const load = useCallback(() => {
    setLoading(true);
    const params: Record<string, any> = {};
    if (dateFilter) params.date = dateFilter;
    if (statusFilter) params.status = statusFilter;
    if (slotFilter) params.time_slot = slotFilter;
    if (search.trim()) params.search = search.trim();
    ordersAPI
      .list(params)
      .then((res) => setOrders(res.data.orders || []))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, [dateFilter, statusFilter, slotFilter, search]);

  useEffect(() => {
    const id = setTimeout(load, 250); // debounce search/filter changes
    return () => clearTimeout(id);
  }, [load]);

  const pushToPrep = async (order: Order) => {
    try {
      await prepStart(order.id);
      load();
    } catch {
      /* ignore */
    }
  };

  const prepStart = async (id: string) => {
    const { prepAPI } = await import('../services/api');
    return prepAPI.start(id);
  };

  // Group by time slot for display.
  const grouped = SLOT_ORDER.map((slot) => ({
    slot,
    items: orders.filter((o) => o.delivery_time_slot === slot),
  })).filter((g) => g.items.length > 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('orderBoard.title')}</h1>
          <p className="mt-1 text-gray-500 dark:text-gray-400">{t('orderBoard.subtitle')}</p>
        </div>
        {canWrite && (
          <button
            onClick={() => navigate('/orders/new')}
            className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700"
          >
            <PlusIcon className="h-5 w-5" />
            {t('orderBoard.newOrder')}
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('orderBoard.searchPlaceholder')}
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-rose-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          />
        </div>
        <input
          type="date"
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:ring-2 focus:ring-rose-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:ring-2 focus:ring-rose-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
        >
          <option value="">{t('orderBoard.allStatuses')}</option>
          {ORDER_STATUSES.map((s) => (
            <option key={s} value={s}>{t(`order.status.${s}`)}</option>
          ))}
        </select>
        <select
          value={slotFilter}
          onChange={(e) => setSlotFilter(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:ring-2 focus:ring-rose-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
        >
          <option value="">{t('orderBoard.allSlots')}</option>
          {SLOT_ORDER.map((s) => (
            <option key={s} value={s}>{t(`order.slot.${s}`)}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-500 dark:text-gray-400">{t('common.loading')}</div>
      ) : grouped.length === 0 ? (
        <div className="rounded-xl border border-gray-100 bg-white py-12 text-center shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white">{t('orderBoard.empty')}</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('orderBoard.emptyHint')}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map((group) => (
            <div key={group.slot} className="rounded-xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center justify-between border-b border-gray-200 px-6 py-3 dark:border-gray-700">
                <h2 className="font-semibold text-gray-900 dark:text-white">{t(`order.slot.${group.slot}`)}</h2>
                <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">
                  {group.items.length}
                </span>
              </div>
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {group.items.map((o) => (
                  <div
                    key={o.id}
                    className="flex flex-wrap items-center gap-3 px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  >
                    <div className="min-w-[180px] flex-1">
                      <button
                        onClick={() => navigate(`/orders/${o.id}`)}
                        className="text-left font-medium text-gray-900 hover:text-rose-600 dark:text-white"
                      >
                        {o.recipient_name}
                      </button>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{o.delivery_address}</div>
                    </div>
                    <div className="hidden text-sm text-gray-600 dark:text-gray-300 sm:block">{o.arrangement_type || '—'}</div>
                    <span className="rounded px-2 py-1 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300 bg-gray-100">
                      {t(`order.source.${o.source}`)}
                    </span>
                    <span className={`rounded-full px-3 py-1 text-xs font-medium ${STATUS_BADGE[o.status] || STATUS_BADGE.new}`}>
                      {t(`order.status.${o.status}`)}
                    </span>
                    <div className="hidden w-16 text-right text-sm text-gray-600 dark:text-gray-300 md:block">
                      {formatMoney(o.order_value_cents)}
                    </div>
                    {canWrite && o.status === 'new' && (
                      <button
                        onClick={() => pushToPrep(o)}
                        className="rounded-lg bg-rose-50 px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-100 dark:bg-rose-900/30 dark:text-rose-300"
                      >
                        {t('orderBoard.pushToPrep')}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrderBoard;
